import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Settings } from '../types';

type SettingsValues = Partial<Omit<Settings, 'id' | 'organization_id'>>;

interface UseSettingsReturn {
  settings: Settings;
  updateSettings: (values: SettingsValues) => Promise<boolean>;
}

const DEFAULT_SETTINGS: Settings = {
  default_start_time: '08:00',
  default_end_time: '17:00',
  default_machine_start_time: '08:00',
  default_machine_end_time: '22:00',
  machine_end_date_definitive: false,
  machine_default_ordered: false,
  order_email_subject: null,
  order_email_body: null,
  return_email_subject: null,
  return_email_body: null,
};

export function useOrgSettings(orgId: string | null): UseSettingsReturn {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);

  const loadSettings = async () => {
    if (!orgId) return;
    try {
      const { data } = await supabase
        .from('settings')
        .select('*')
        .eq('organization_id', orgId)
        .maybeSingle();
      if (data) setSettings(data);
    } catch {
      // Non-critical: UI defaults are safe fallbacks when settings can't be loaded
    }
  };

  useEffect(() => {
    loadSettings();
  }, [orgId]);

  const updateSettings = async (values: SettingsValues): Promise<boolean> => {
    try {
      if (settings.id) {
        const { error } = await supabase.from('settings').update(values).eq('id', settings.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('settings')
          .insert({ ...DEFAULT_SETTINGS, ...values, organization_id: orgId });
        if (error) throw error;
      }
      await loadSettings();
      return true;
    } catch {
      return false;
    }
  };

  return { settings, updateSettings };
}
